import { Link } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/SiteHeader";
import VideoBanner, { SidebarVideoBanners } from "@/components/blackbox/VideoBanner";
import { blogArticles } from "@/data/blogArticles";
import { BookOpen, Clock, Tag, ArrowRight, FileText } from "lucide-react";

const Blog = () => {
  // Group articles by category
  const categories = [...new Set(blogArticles.map(a => a.category))];
  const featured = blogArticles[0];

  // Schema markup for blog
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog de Saúde Masculina – PPP, Glândulas de Tyson e Saúde P-nile",
    "description": "Artigos educativos sobre pápulas perladas p-nilianas, glândulas de Tyson, manchas de Fordyce e saúde genital masculina.",
    "url": "https://smothmethod.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Método P-nis Liso"
    },
    "blogPost": blogArticles.map(article => ({
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.metaDescription,
      "url": `https://smothmethod.com/blog/${article.slug}`,
      "datePublished": article.publishedAt,
      "dateModified": article.updatedAt
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Início",
        "item": "https://smothmethod.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://smothmethod.com/blog"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <SidebarVideoBanners />
      <Helmet>
        <title>Blog: PPP, Glândulas de Tyson e Saúde Masculina</title>
        <meta name="description" content="Artigos educativos sobre PPP, glândulas de Tyson, manchas de Fordyce e saúde genital masculina baseados em evidências clínicas." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://smothmethod.com/blog" />
        <link rel="alternate" hrefLang="pt-br" href="https://smothmethod.com/blog" />
        <link rel="alternate" hrefLang="x-default" href="https://smothmethod.com/blog" />
        <meta property="og:locale" content="pt_BR" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blog de Saúde Masculina | Artigos sobre PPP e Glândulas de Tyson" />
        <meta property="og:description" content="Artigos educativos sobre pápulas perladas p-nilianas, glândulas de Tyson e saúde genital masculina." />
        <meta property="og:url" content="https://smothmethod.com/blog" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog de Saúde Masculina | PPP e Glândulas de Tyson" />
        <meta name="twitter:description" content="Artigos educativos sobre PPP, glândulas de Tyson e saúde masculina." />

        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <SiteHeader />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/" className="hover:text-[#4267B2]">Início</Link>
            </li>
            <li className="mx-1">/</li>
            <li className="text-gray-700 font-medium">Blog</li>
          </ol>
        </nav>

        {/* Hero Section */}
        <header className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#4267B2]/10 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#4267B2]" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Blog de Saúde Masculina
              </h1>
              <p className="text-gray-600">Artigos e Guias Educativos</p>
            </div>
          </div>
          <p className="text-gray-600">
            Informação especializada sobre pápulas perladas p-nilianas (PPP), glândulas de Tyson, manchas de Fordyce e outras dúvidas comuns.
            Todo o conteúdo é baseado em pesquisa e escrito para ajudar você a entender seu corpo.
          </p>
          
          {/* Quick Stats */}
          <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FileText className="w-4 h-4" />
              <span>{blogArticles.length} Artigos</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Tag className="w-4 h-4" />
              <span>{categories.length} Categorias</span>
            </div>
          </div>
        </header>

        {/* Featured Article */}
        {featured && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Artigo em Destaque</h2>
            <Link 
              to={`/blog/${featured.slug}`}
              className="block bg-gradient-to-r from-[#4267B2] to-[#5b7bd5] text-white rounded-lg p-6 hover:opacity-95 transition-opacity"
            >
              <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">
                {featured.category}
              </span>
              <h3 className="text-xl md:text-2xl font-bold mt-3 mb-2">
                {featured.title}
              </h3>
              <p className="text-white/80 mb-4">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-4 text-sm text-white/70">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {featured.readTime} de leitura
                </span>
                <span className="flex items-center gap-1">
                  <ArrowRight className="w-4 h-4" />
                  Ler Artigo
                </span>
              </div>
            </Link>
          </section>
        )}

        {/* Category Sections */}
        {categories.map(category => (
          <section key={category} className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#4267B2]" />
              {category}
            </h2>
            <div className="space-y-3">
              {blogArticles
                .filter(a => a.category === category)
                .map(article => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2 hover:text-[#4267B2]">
                          {article.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {article.readTime}
                          </span>
                          <span>Atualizado em {article.updatedAt}</span>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 shrink-0 mt-1" />
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        ))}

        {/* CTA Section */}
        <section className="bg-white border border-gray-200 rounded-lg p-6 mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Tem dúvidas?
          </h2>
          <p className="text-gray-600 mb-4">
            Confira nosso FAQ para respostas rápidas ou navegue pelos nossos artigos de saúde.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link 
              to="/perguntas-frequentes" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#4267B2] text-white rounded-lg hover:bg-[#365899] transition-colors text-sm font-medium"
            >
              Ver FAQ
            </Link>
            <Link 
              to="/artigos" 
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Ver Artigos
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center py-6 mt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Este blog é apenas para fins educativos. Sempre consulte um profissional médico em caso de dúvidas de saúde.
          </p>
        </footer>

        <VideoBanner />
      </main>
    </div>
  );
};

export default Blog;
